import Cookies from "js-cookie";

export const COOKIE_CONSENT_NAME = "cookies";
export const COOKIE_CONSENT_VERSION = 1;
export const COOKIE_CONSENT_EXPIRES_DAYS = 180;
export const FUNCTIONAL_COOKIE_EXPIRES_DAYS = 180;

const LEGACY_APPROVED_CONSENT_VALUE = "approved";

const FUNCTIONAL_COOKIE_NAMES = Object.freeze([
  "language",
  "theme",
]);

const FUNCTIONAL_COOKIE_PREFIXES = Object.freeze([
  "regionsLoading-",
  "regionsUnloading-",
  "selectedConsignors-",
  "excludedConsignors-",
]);

const functionalSessionValues = new Map();

function isSecureProtocol() {
  return typeof window !== "undefined" && window.location.protocol === "https:";
}

function getCookieAttributes(expires) {
  return {
    expires,
    path: "/",
    sameSite: "lax",
    secure: isSecureProtocol(),
  };
}

function createConsent(functional, analytics) {
  if (typeof functional !== "boolean" || typeof analytics !== "boolean") {
    throw new TypeError("Cookie consent categories must be boolean values");
  }

  return {
    version: COOKIE_CONSENT_VERSION,
    functional,
    analytics,
  };
}

function parseConsent(rawConsent) {
  if (!rawConsent) {
    return null;
  }

  let parsedConsent;

  try {
    parsedConsent = JSON.parse(rawConsent);
  } catch (error) {
    return null;
  }

  if (
    !parsedConsent ||
    Array.isArray(parsedConsent) ||
    typeof parsedConsent !== "object"
  ) {
    return null;
  }

  if (parsedConsent.version !== COOKIE_CONSENT_VERSION) {
    return null;
  }

  if (
    typeof parsedConsent.functional !== "boolean" ||
    typeof parsedConsent.analytics !== "boolean"
  ) {
    return null;
  }

  return createConsent(
    parsedConsent.functional,
    parsedConsent.analytics
  );
}

function isKnownFunctionalCookie(cookieName) {
  if (typeof cookieName !== "string" || !cookieName) {
    return false;
  }

  if (FUNCTIONAL_COOKIE_NAMES.includes(cookieName)) {
    return true;
  }

  return FUNCTIONAL_COOKIE_PREFIXES.some((prefix) => (
    cookieName.startsWith(prefix)
  ));
}

function assertKnownFunctionalCookie(cookieName) {
  if (!isKnownFunctionalCookie(cookieName)) {
    throw new Error(`Unknown functional cookie: ${cookieName}`);
  }
}

function clearPersistedFunctionalCookies() {
  const storedCookies = Cookies.get();

  Object.keys(storedCookies)
    .filter(isKnownFunctionalCookie)
    .forEach((cookieName) => {
      Cookies.remove(
        cookieName,
        getCookieAttributes(FUNCTIONAL_COOKIE_EXPIRES_DAYS)
      );
    });
}

function persistFunctionalSessionValues() {
  functionalSessionValues.forEach((value, cookieName) => {
    Cookies.set(
      cookieName,
      value,
      getCookieAttributes(FUNCTIONAL_COOKIE_EXPIRES_DAYS)
    );
  });
}

function saveConsent({functional, analytics}) {
  const consent = createConsent(functional, analytics);

  Cookies.set(
    COOKIE_CONSENT_NAME,
    JSON.stringify(consent),
    getCookieAttributes(COOKIE_CONSENT_EXPIRES_DAYS)
  );

  if (consent.functional) {
    persistFunctionalSessionValues();
  } else {
    clearPersistedFunctionalCookies();
  }

  return consent;
}

function getConsent() {
  const rawConsent = Cookies.get(COOKIE_CONSENT_NAME);

  if (!rawConsent) {
    clearPersistedFunctionalCookies();

    return null;
  }

  if (rawConsent === LEGACY_APPROVED_CONSENT_VALUE) {
    return saveConsent({
      functional: true,
      analytics: true,
    });
  }

  const consent = parseConsent(rawConsent);

  if (!consent) {
    clearPersistedFunctionalCookies();
  }

  return consent;
}

function hasConsentDecision() {
  return getConsent() !== null;
}

function canUseNecessaryCookies() {
  return true;
}

function canUseFunctionalCookies() {
  const consent = getConsent();

  return Boolean(consent && consent.functional);
}

function canUseAnalyticsCookies() {
  const consent = getConsent();

  return Boolean(consent && consent.analytics);
}

function getFunctionalCookie(cookieName) {
  assertKnownFunctionalCookie(cookieName);

  if (functionalSessionValues.has(cookieName)) {
    return functionalSessionValues.get(cookieName);
  }

  if (!canUseFunctionalCookies()) {
    return undefined;
  }

  const storedValue = Cookies.get(cookieName);

  if (typeof storedValue === "string") {
    functionalSessionValues.set(cookieName, storedValue);
  }

  return storedValue;
}

function setFunctionalCookie(cookieName, value) {
  assertKnownFunctionalCookie(cookieName);

  if (typeof value !== "string") {
    throw new TypeError("Functional cookie value must be a string");
  }

  functionalSessionValues.set(cookieName, value);

  if (!canUseFunctionalCookies()) {
    return false;
  }

  Cookies.set(
    cookieName,
    value,
    getCookieAttributes(FUNCTIONAL_COOKIE_EXPIRES_DAYS)
  );

  return true;
}

function removeFunctionalCookie(cookieName) {
  assertKnownFunctionalCookie(cookieName);

  functionalSessionValues.delete(cookieName);

  Cookies.remove(
    cookieName,
    getCookieAttributes(FUNCTIONAL_COOKIE_EXPIRES_DAYS)
  );
}

export const cookieConsentService = {
  getConsent,
  saveConsent,
  hasConsentDecision,
  canUseNecessaryCookies,
  canUseFunctionalCookies,
  canUseAnalyticsCookies,
  getFunctionalCookie,
  setFunctionalCookie,
  removeFunctionalCookie,
  clearFunctionalCookies: clearPersistedFunctionalCookies,
};
